// TOEFL iBT Mock 2 — Listening (2026 format, multistage adaptive)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Topics are unique to this mock. The harder Module 2 uses indirect replies
// and implied-meaning questions.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", W("Is the computer lab usually this crowded?"), [
      "I bought a new computer.",
      "Only during exam week.",
      "The lab is on the second floor.",
      "Crowds make me nervous at concerts.",
    ], 1),
    response("m1-r2", M("Do you want to work on the presentation together?"), [
      "Sure. How about tomorrow after class?",
      "I presented it last year.",
      "The presentation was about climate.",
      "I work at the library on weekends.",
    ], 0),
    response("m1-r3", W("I left my umbrella in the lecture hall."), [
      "The lecture was about weather.",
      "It's going to be sunny tomorrow.",
      "Let's go back and get it before it's locked.",
      "I bought my umbrella downtown.",
    ], 2),
    response("m1-r4", M("Weren't you supposed to meet your advisor today?"), [
      "My advisor teaches physics.",
      "We met at a party once.",
      "I'm supposed to study tonight.",
      "She had to cancel, so it's next Monday now.",
    ], 3),
    response("m1-r5", W("How about getting some coffee before the seminar?"), [
      "Good idea. We have about twenty minutes.",
      "The seminar was very long.",
      "I don't drink tea.",
      "The coffee machine is broken at home.",
    ], 0),
    response("m1-r6", M("I can't believe how expensive the textbooks are this term."), [
      "The term starts in September.",
      "Have you tried renting them online?",
      "I believe the professor is right.",
      "The bookstore is very large.",
    ], 1),
    response("m1-r7", W("The bus was so late that I missed the first half of class."), [
      "I'll take the bus tomorrow.",
      "The class has twenty students.",
      "Do you want to borrow my notes?",
      "The first half was in the morning.",
    ], 2),
    response("m1-r8", M("Shouldn't we start studying for the final soon?"), [
      "The final is a long word.",
      "I studied abroad last year.",
      "We finished the course.",
      "You're right. Let's make a plan this weekend.",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Changing a Lab Section",
      lines: [
        M("Hi, I'm in Biology 101, and I need to switch my lab section. Can you help me with that?"),
        W("I can try. Which section are you in now?"),
        M("The Wednesday afternoon one. I just got a part-time job at the campus bookstore, and my shifts are on Wednesday afternoons."),
        W("Let me check. The Monday morning section is full, but there's one space left in the Friday afternoon section."),
        M("Friday works. Do I need any special permission to switch?"),
        W("You'll need a signature from your lab instructor on this form. Once it's signed, bring it back here and I'll make the change in the system."),
        M("Okay. Is there a deadline?"),
        W("The end of this week. After that, sections are locked for the rest of the semester. And I'd move quickly, since that's the last open space."),
      ],
      questions: [
        {
          question: "Why does the man want to change his lab section?",
          options: [
            "He doesn't like his lab partner.",
            "His new job conflicts with his current section.",
            "His current section was cancelled.",
            "He wants to take the lab in the morning.",
          ],
          correct: 1,
        },
        {
          question: "What does the man need to do before the change can be made?",
          options: [
            "Pay a small fee",
            "Talk to his employer",
            "Get his lab instructor to sign a form",
            "Join the Monday waiting list",
          ],
          correct: 2,
        },
        {
          question: "Why does the woman tell the man to move quickly?",
          options: [
            "Someone else might take the last space.",
            "The office is closing early today.",
            "His lab instructor is going away.",
            "The Friday section starts tomorrow.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Campus Blood Drive",
      lines: [
        W("Hello, everyone. The campus blood drive will take place next Tuesday and Wednesday from ten a.m. to four p.m. in the Student Union ballroom. Donating takes about an hour, including a short health check. Donors must be at least seventeen years old and should eat a good meal and drink plenty of water beforehand. You can book a time slot on the health center website, but walk-ins are also welcome. Every donor will receive a free T-shirt and snacks after donating."),
      ],
      questions: [
        {
          question: "What is the main purpose of the announcement?",
          options: [
            "To recruit volunteers for the health center",
            "To announce a new Student Union ballroom",
            "To give information about a blood drive",
            "To sell T-shirts for a charity",
          ],
          correct: 2,
        },
        {
          question: "What are donors advised to do before donating?",
          options: [
            "Skip breakfast",
            "Eat well and drink water",
            "Bring a T-shirt",
            "Get a letter from a doctor",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Psychology: Why We Remember Unfinished Tasks",
      lines: [
        W("In the 1920s, a psychologist named Bluma Zeigarnik noticed something interesting in a busy restaurant in Vienna. The waiters could remember long, complicated orders perfectly, as long as the bill hadn't been paid yet. But once the customers paid, the waiters seemed to forget the orders almost immediately. Zeigarnik wondered whether people remember unfinished tasks better than finished ones. To test this, she gave people a series of simple tasks, like solving puzzles or making clay figures, and interrupted them during some of the tasks. Later, when she asked them what they had worked on, they recalled the interrupted tasks about twice as often as the completed ones. This became known as the Zeigarnik effect. One explanation is that starting a task creates a kind of mental tension, which keeps the task active in our memory until it's finished. Now, this has a practical use for students. If you're having trouble starting a big assignment, just begin it, even for ten minutes. Once it's started, your mind is more likely to keep returning to it, and that makes it easier to continue."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How restaurants train their waiters",
            "Why people remember unfinished tasks better than finished ones",
            "How to solve puzzles more quickly",
            "The history of psychology in Vienna",
          ],
          correct: 1,
        },
        {
          question: "Why does the professor mention the waiters in Vienna?",
          options: [
            "To describe the observation that led to Zeigarnik's research",
            "To show that waiters have unusually good memories",
            "To explain why customers forget to pay",
            "To compare restaurants in different countries",
          ],
          correct: 0,
        },
        {
          question: "What did Zeigarnik find in her experiment?",
          options: [
            "People remembered completed tasks better.",
            "People forgot all of the tasks quickly.",
            "People preferred clay figures to puzzles.",
            "People recalled interrupted tasks about twice as often.",
          ],
          correct: 3,
        },
        {
          question: "What does the professor recommend to students?",
          options: [
            "Finish every task before taking a break",
            "Avoid working on more than one assignment",
            "Start a large assignment, even for a short time",
            "Study only in quiet places",
          ],
          correct: 2,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", M("I'd hate to be in Paul's shoes when the results come out."), [
      "Why? Do you think his experiment failed?",
      "His shoes are brand new.",
      "The results came out last week.",
      "I'd hate to buy new shoes.",
    ], 0),
    response("up-r2", W("Don't you think the deadline is a little unrealistic?"), [
      "The deadline is next Friday.",
      "I didn't think about the realism of the film.",
      "It's tight, but I think we can manage it.",
      "I'm a little tired today.",
    ], 2),
    response("up-r3", M("If only I'd backed up my files before my laptop crashed."), [
      "I backed my car into a wall.",
      "You lost everything? That's awful.",
      "My laptop is quite old.",
      "The files are in the cabinet.",
    ], 1),
    response("up-r4", W("You'd think the department would have told us about the room change."), [
      "The department has twelve rooms.",
      "I think I changed my mind.",
      "We told them about the change.",
      "I know. I only found out from a classmate.",
    ], 3),
    response("up-r5", M("I'm not sure that argument would hold up in front of the committee."), [
      "The committee meets on Thursday.",
      "Hold the door, please.",
      "Then maybe I should find more evidence.",
      "I held up my hand to ask a question.",
    ], 2),
    {
      id: "up-conv",
      type: "conversation",
      title: "Borrowing Equipment for Field Research",
      lines: [
        W("Hi, I'm doing field research on bird populations for my geography thesis, and I was hoping to borrow one of the department's GPS units for the summer."),
        M("We do lend them out, but normally only for up to two weeks at a time. How long would you need it?"),
        W("About two months. My study sites are in the mountains, and I need to visit each one several times."),
        M("Two months is a long time. The units are in high demand during the summer field season. Has your supervisor approved the project?"),
        W("Yes, Dr. Nolan approved it last month."),
        M("Then here's an option. If Dr. Nolan sends us an email confirming that the equipment is essential for your thesis, we can treat it as a research loan instead of a regular one. Research loans can be extended for the whole summer."),
        W("That's great. Is there anything else I need to do?"),
        M("You'll have to sign a damage agreement. The units are expensive, and mountain conditions can be rough on them, so I'd also recommend buying a waterproof case."),
      ],
      questions: [
        {
          question: "What does the woman want to do?",
          options: [
            "Buy a new GPS unit",
            "Borrow equipment for longer than usual",
            "Change her thesis topic",
            "Join a field trip to the mountains",
          ],
          correct: 1,
        },
        {
          question: "What does the man suggest the woman do?",
          options: [
            "Ask her supervisor to confirm the need for the equipment",
            "Return the unit every two weeks",
            "Use her own phone instead",
            "Wait until the field season ends",
          ],
          correct: 0,
        },
        {
          question: "Why does the man recommend a waterproof case?",
          options: [
            "It is required for all research loans.",
            "The GPS unit does not work in the rain.",
            "Mountain conditions could damage the equipment.",
            "It makes the unit easier to carry.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "Architecture: The Engineering of Gothic Cathedrals",
      lines: [
        M("Gothic cathedrals, built in Europe from the twelfth century onward, are famous for their height and their enormous stained-glass windows. But those two features created a serious engineering problem. A tall stone roof is extremely heavy, and its weight doesn't just push downward; it also pushes outward on the walls. In earlier Romanesque churches, builders handled this outward push by making the walls very thick, with only small windows. That worked, but the interiors were dark. Gothic builders wanted the opposite: thin walls filled with glass. Their solution was the flying buttress. A flying buttress is an arch of stone that leaps from the upper part of the wall to a heavy pillar standing outside the building. It catches the outward push of the roof and carries it down to the ground, away from the wall. Because the walls no longer had to carry that force, large sections of them could be replaced with windows. Interestingly, builders didn't fully understand the physics involved. They worked largely by experience, and some cathedrals collapsed during construction. The roof of Beauvais Cathedral, which was the tallest of its time, fell in 1284, and it had to be rebuilt with extra buttresses."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How stained glass is made",
            "Differences in religious practice in medieval Europe",
            "Why Beauvais Cathedral is famous",
            "How Gothic builders supported tall buildings with thin walls",
          ],
          correct: 3,
        },
        {
          question: "Why does the professor mention Romanesque churches?",
          options: [
            "To contrast an earlier approach to the same engineering problem",
            "To show that they were taller than Gothic cathedrals",
            "To explain where stained glass was invented",
            "To describe a church that collapsed",
          ],
          correct: 0,
        },
        {
          question: "According to the professor, what does a flying buttress do?",
          options: [
            "It holds up the stained-glass windows directly.",
            "It makes the roof lighter.",
            "It carries the outward push of the roof to the ground.",
            "It allows light to enter through the roof.",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from the professor's comments about Beauvais Cathedral?",
          options: [
            "It was built without any flying buttresses.",
            "Its builders pushed the design beyond what their experience could safely support.",
            "It was the first Gothic cathedral ever built.",
            "It was destroyed during a war.",
          ],
          correct: 1,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", W("What time is it?"), [
      "It's almost three o'clock.",
      "I don't have a watch.",
      "It's Tuesday.",
      "Time goes fast.",
    ], 0),
    response("low-r2", M("Where do you live?"), [
      "I live alone.",
      "I've lived there for two years.",
      "In an apartment near the park.",
      "I like my home.",
    ], 2),
    response("low-r3", W("Can you help me carry these boxes?"), [
      "The boxes are brown.",
      "Of course. Where are they going?",
      "I carried my bag.",
      "I have three boxes at home.",
    ], 1),
    response("low-r4", M("Have you eaten lunch yet?"), [
      "I like pizza.",
      "Lunch is at noon.",
      "The cafeteria is busy.",
      "Not yet. Do you want to go now?",
    ], 3),
    response("low-r5", W("How long is the movie?"), [
      "About two hours.",
      "It's a funny movie.",
      "I saw it yesterday.",
      "The theater is big.",
    ], 0),
    {
      id: "low-ann",
      type: "announcement",
      title: "Soccer Team Tryouts",
      lines: [
        M("Attention, students. Tryouts for the university soccer team will be held next Monday and Tuesday at four p.m. on the North Field. All students are welcome to try out. Please bring sports shoes, a water bottle, and your student ID. If it rains, tryouts will move to the indoor gym. For more information, visit the sports office."),
      ],
      questions: [
        {
          question: "What is the announcement about?",
          options: ["A soccer game", "Soccer team tryouts", "A new sports office", "A sports shoe sale"],
          correct: 1,
        },
        {
          question: "What will happen if it rains?",
          options: [
            "Tryouts will be canceled.",
            "Tryouts will start later.",
            "Tryouts will move to the indoor gym.",
            "Tryouts will be held on Wednesday.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Food Science: Why Bread Rises",
      lines: [
        W("Have you ever wondered why bread is soft and full of tiny holes? The answer is yeast. Yeast is a very small living thing, so small that you need a microscope to see it. When bakers mix yeast with flour, water and a little sugar, the yeast starts to eat the sugar. As it eats, it makes a gas called carbon dioxide. The gas gets trapped in the dough and makes it grow bigger. This is called rising. When the bread is baked, the heat kills the yeast, but the holes made by the gas stay. That's why bread is light and soft."),
      ],
      questions: [
        {
          question: "What makes bread rise?",
          options: ["Salt", "Gas made by yeast", "Hot water", "Milk"],
          correct: 1,
        },
        {
          question: "What does yeast eat?",
          options: ["Flour only", "Salt", "Water", "Sugar"],
          correct: 3,
        },
        {
          question: "What happens to the yeast when the bread is baked?",
          options: [
            "The heat kills it.",
            "It makes more gas.",
            "It turns into sugar.",
            "It leaves the bread.",
          ],
          correct: 0,
        },
      ],
    },
  ],
};
